import { Link } from "@/src/common/components/Link"
import { cachedApiCall } from "@/src/common/utils/cachedApiCall"
import {
  URLFactory,
  appendSlash
} from "@/src/sb/business-layer/factories/URLFactory"
import {
  Features,
  SidebarMenuItemV2,
  SidebarSectionConfig
} from "@/src/sb/business-layer/types"
import { useAnalytics } from "@/src/sb/components/Analytics"
import AngleDown from "@/src/sb/components/icon/AngleDown"
import { StearsLogoNode } from "@/src/sb/components/layouts/DefaultLayoutV3"
import { SIDEBAR_INDUSTRY_SLUGS_TO_HIDE } from "@/src/sb/constants"
import { OPEN_DATA_PAGE_BASE_PATH } from "@/src/sb/constants/configs"
import { accounts } from "@/src/sb/constants/copy/navItems"
import {
  ENABLE_INDUSTRY_PROFILE_LINKS,
  SIDEBAR_CLASSIFICATION_TO_SECTION,
  SIDEBAR_COUNTRIES_CLASS_NAME,
  SIDEBAR_INDUSTRIES_CLASS_NAME,
  SIDEBAR_MAX_DEPTH,
  SIDEBAR_SECTIONS
} from "@/src/sb/constants/featureFlags"
import { SBAPIInstance } from "@/src/sb/data-layer"
import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import classNames from "classnames"
import { useRouter } from "next/router"
import { ReactNode, useEffect, useMemo, useState } from "react"
import useSWR from "swr"

import SidebarUserAccount from "./SidebarUserAccount"

type SidebarV2Props = {
  onClose?: () => void
  asideFooter?: ReactNode | null
  sidebarMenuItems: { [key: string]: SidebarMenuItemV2[] }
  sidebarRootItemsToHide: Array<number>
  features?: Features
  showUserAccount?: boolean
  userName?: string
  userType?: keyof typeof accounts
  setNextQueryParam?: boolean
  defaultUserAccountOpen?: boolean
}

const SIDEBAR_OPEN_STATE = "sidebarOpenState"

const SidebarV2 = ({
  onClose,
  asideFooter = null,
  sidebarMenuItems,
  sidebarRootItemsToHide,
  features,
  showUserAccount = false,
  userName = "",
  userType = "anonymous",
  setNextQueryParam = false,
  defaultUserAccountOpen = false
}: SidebarV2Props) => {
  const shouldShowUserAccount = showUserAccount && Boolean(userName)

  // Get section configuration from features
  const sidebarSections =
    (features?.[SIDEBAR_SECTIONS] as SidebarSectionConfig[]) || []
  const classificationToSection =
    (features?.[SIDEBAR_CLASSIFICATION_TO_SECTION] as Record<string, string>) ||
    {}

  const updatedSidebarMenuItems = useMemo(() => {
    const base = { ...sidebarMenuItems }
    if (features?.[ENABLE_INDUSTRY_PROFILE_LINKS] && base.industries) {
      base.industries = base.industries.filter(
        (industry) => !SIDEBAR_INDUSTRY_SLUGS_TO_HIDE.includes(industry.slug)
      )
    }
    if (base.countries) {
      base.countries = base.countries.map((subregion) => ({
        ...subregion,
        children: [...(subregion.children ?? [])].sort((a, b) =>
          a.name.localeCompare(b.name)
        )
      }))
    }
    return base
  }, [features, sidebarMenuItems])

  // Organize sidebar items by section
  const organizedSidebarItems = useMemo(() => {
    const result: Record<string, SidebarMenuItemV2[]> = {}

    for (const [classification, items] of Object.entries(
      updatedSidebarMenuItems
    )) {
      for (const item of items) {
        // Priority: item.section > classification mapping > classification key
        const sectionLabel =
          item.section ||
          classificationToSection[classification] ||
          classification

        if (!result[sectionLabel]) {
          result[sectionLabel] = []
        }

        result[sectionLabel].push({
          ...item,
          section: sectionLabel
        })
      }
    }

    return result
  }, [updatedSidebarMenuItems, classificationToSection])

  // Get ordered sections from features, falling back to organizedSidebarItems keys
  const orderedSections = useMemo<SidebarSectionConfig[]>(() => {
    if (sidebarSections.length > 0) {
      return sidebarSections
        .sort((a, b) => a.order - b.order)
        .filter((section) => organizedSidebarItems[section.id]?.length > 0)
    }
    // Fallback: use Object.keys order if no sections configured
    return Object.keys(organizedSidebarItems).map((key, index) => ({
      id: key,
      label: key,
      order: index
    }))
  }, [sidebarSections, organizedSidebarItems])

  const tagClassificationNames: Record<string, any> = {
    countries: features?.[SIDEBAR_COUNTRIES_CLASS_NAME],
    industries: features?.[SIDEBAR_INDUSTRIES_CLASS_NAME]
  }

  const analytics = useAnalytics()
  const [isMobile] = useWindowSize()
  const router = useRouter()
  const [sidebarOpenState, setSidebarOpenState] = useState<
    Record<string, boolean>
  >({})

  const { data: countryList } = useSWR(
    ["countries_list", { limit: 54, offset: 0 }],
    () =>
      cachedApiCall("countries-list", () =>
        SBAPIInstance.fetchCountries({ limit: 54, offset: 0 })
      ),
    {
      revalidateOnFocus: false,
      dedupingInterval: 300000 // 5 minutes
    }
  )

  const availableCountrySlugs = useMemo(
    () => countryList?.items?.map((country: any) => country.slug) || [],
    [countryList]
  )

  const persistSidebarOpenState = (openState: Record<string, boolean>) => {
    setSidebarOpenState(openState)
    localStorage.setItem(SIDEBAR_OPEN_STATE, JSON.stringify(openState))
  }

  const getDestination = (
    menuItem: Pick<SidebarMenuItemV2, "destination" | "slug">,
    classification: string
  ) =>
    menuItem.destination
      ? appendSlash(menuItem.destination)
      : classification === "countries" &&
        availableCountrySlugs.includes(menuItem.slug)
      ? URLFactory.country(menuItem.slug)
      : classification === "industries" &&
        features?.[ENABLE_INDUSTRY_PROFILE_LINKS]
      ? `/industries/${menuItem.slug}/countries/nigeria`
      : `/search?tags=${menuItem.slug}`

  const hasCurrentRoute = (
    menuItem: SidebarMenuItemV2,
    classification: string
  ) => {
    const destination = getDestination(menuItem, classification)

    return (
      (router.asPath.startsWith("/wf")
        ? router.asPath.replace("/wf", "")
        : router.asPath) === destination ||
      (router.query.tags as string) === menuItem.slug
    )
  }

  const renderSidebarMenuItemChildren = (
    sidebarMenuItems: Array<SidebarMenuItemV2> | undefined,
    classification: string,
    depth = 1,
    maxDepth = Number(features?.[SIDEBAR_MAX_DEPTH])
  ) => {
    return sidebarMenuItems?.length
      ? sidebarMenuItems.map((sidebarMenuItemChild: SidebarMenuItemV2) => {
          if (!sidebarMenuItemChild.isSidebarItem) return null
          const hasChildren = sidebarMenuItemChild.children?.filter(
            (item) => item.isSidebarItem
          ).length

          const menuItemGeneratedKey: string =
            sidebarMenuItemChild.id + "-" + sidebarMenuItemChild.name

          const item = (
            <>
              <span className="flex items-center gap-2">
                {sidebarMenuItemChild.name}
                {sidebarMenuItemChild.badge && (
                  <span
                    className={classNames(
                      "p-[4px] rounded-[2px]",
                      "font-jakarta text-[10px] font-medium leading-[12px] tracking-[0.2px]",
                      "[leading-trim:both] [text-edge:cap]",
                      sidebarMenuItemChild.isHighlightedSidebarItem
                        ? "bg-gray-200 text-gray-900 dark:bg-[#0E0F10] dark:text-white"
                        : "bg-gray-700 text-white dark:bg-white dark:text-black"
                    )}
                  >
                    {sidebarMenuItemChild.badge}
                  </span>
                )}
              </span>
              {hasChildren && depth < maxDepth ? (
                <span
                  className={classNames(
                    "inline-block shrink-0 transition-all",
                    { "-rotate-90": !sidebarOpenState[menuItemGeneratedKey] }
                  )}
                >
                  <AngleDown />
                </span>
              ) : null}
            </>
          )

          const itemProps = {
            className: classNames(
              "pr-5 cursor-pointer flex items-center gap-4 justify-between",
              "transition-all dark:hover:bg-[#1A1D21] dark:hover:text-stears_white hover:!no-underline dark:hover:no-underline",
              hasCurrentRoute(sidebarMenuItemChild, classification)
                ? "!text-gray-900 dark:!text-stears_white dark:bg-[#1A1D21]"
                : "!text-gray-700 dark:!text-[#9CA3AF]",
              {
                "text-sm leading-[22px] font-medium pl-5 h-7": depth === 1,
                "text-xs leading-[16px] py-[6px] py-[6px]": depth > 1,
                "pl-[30px]": depth === 2,
                "pl-[40px]": depth === 3,
                "pl-[50px]": depth === 4,
                "text-stears_white dark:text-stears_white dark:text-stears_white ":
                  sidebarMenuItemChild?.slug === "screener"
              }
            )
          }

          return (
            <section key={menuItemGeneratedKey}>
              {hasChildren && depth < maxDepth ? (
                <section
                  {...itemProps}
                  onClick={() =>
                    persistSidebarOpenState({
                      ...sidebarOpenState,
                      [menuItemGeneratedKey]:
                        !sidebarOpenState[menuItemGeneratedKey]
                    })
                  }
                >
                  {item}
                </section>
              ) : (
                <Link
                  {...itemProps}
                  href={getDestination(sidebarMenuItemChild, classification)}
                  onClick={(e) => {
                    if (sidebarMenuItemChild.isHighlightedSidebarItem) {
                      const dest = getDestination(
                        sidebarMenuItemChild,
                        classification
                      )
                      const normalizedPath = router.asPath.startsWith("/wf")
                        ? router.asPath.replace("/wf", "")
                        : router.asPath
                      const normalizedDest = dest.endsWith("/")
                        ? dest.slice(0, -1)
                        : dest
                      if (
                        normalizedPath === normalizedDest ||
                        normalizedPath.startsWith(normalizedDest + "/")
                      ) {
                        e.preventDefault()
                        return
                      }
                    }

                    onClose?.()

                    analytics?.clickNavItem("menu_item", {
                      label: sidebarMenuItemChild.name,
                      destination: getDestination(
                        sidebarMenuItemChild,
                        classification
                      ),
                      context: "sidebar"
                    })
                  }}
                >
                  <span
                    className={classNames(
                      "block transition-all rounded-[3px]",
                      sidebarMenuItemChild?.isHighlightedSidebarItem
                        ? "bg-gray-700 text-stears_white dark:bg-stears_white dark:text-black font-semibold px-2 py-1 shadow-md text-sm"
                        : ""
                    )}
                  >
                    {item}
                  </span>
                </Link>
              )}

              {sidebarOpenState[menuItemGeneratedKey] && depth < maxDepth
                ? renderSidebarMenuItemChildren(
                    sidebarMenuItemChild.children,
                    classification,
                    depth + 1,
                    maxDepth
                  )
                : null}
            </section>
          )
        })
      : null
  }

  useEffect(() => {
    const persistedSidebarOpenState = localStorage.getItem(SIDEBAR_OPEN_STATE)
    if (persistedSidebarOpenState === null) return
    setSidebarOpenState(JSON.parse(persistedSidebarOpenState))
  }, [])

  // Helper to get the original classification key for a section
  const getSectionClassification = (sectionId: string): string => {
    for (const [classification, mappedSection] of Object.entries(
      classificationToSection
    )) {
      if (mappedSection === sectionId) {
        return classification
      }
    }
    return sectionId.toLowerCase()
  }

  return (
    <>
      {!isMobile ? (
        <section
          data-testid="sidebar-brand-header"
          className={
            "flex gap-4 justify-between items-center p-5 border-b-[0.5px] dark:border-gray-600 shrink-0 h-[80px] sticky md:static top-0 left-0 right-0 z-20 leading-[20px]"
          }
        >
          <StearsLogoNode useSVG height={21.3} />
        </section>
      ) : null}

      {shouldShowUserAccount && (
        <SidebarUserAccount
          userName={userName}
          userType={userType}
          setNextQueryParam={setNextQueryParam}
          onClose={onClose}
          defaultOpen={defaultUserAccountOpen}
        />
      )}

      <section
        className={classNames(
          "flex flex-col relative min-h-0 overflow-hidden",
          shouldShowUserAccount
            ? "h-[calc(100dvh-60px)] md:h-[calc(100vh-80px-60px)]"
            : "h-[100dvh] md:h-[calc(100vh-80px)]"
        )}
      >
        <section
          data-testid="sidebar-menu-scroll"
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden no-scrollbar"
        >
          {orderedSections
            .filter((section, idx) => {
              const classification = getSectionClassification(section.id)
              if (
                classification !== "open data" &&
                router.asPath.includes(OPEN_DATA_PAGE_BASE_PATH)
              )
                return false

              if (!isMobile || !sidebarRootItemsToHide.includes(idx))
                return true

              return false
            })
            .map((section) => {
              const items = organizedSidebarItems[section.id]
              if (!items?.length) return null

              const classification = getSectionClassification(section.id)
              const sectionLabel =
                tagClassificationNames?.[classification] ?? section.label

              const itemProps = {
                className: classNames(
                  "pl-4 flex gap-2 items-center text-gray-700",
                  "dark:text-gray-400 border-l-4 border-l-transparent",
                  "transition-all sticky top-0 z-20 bg-stears_white",
                  "dark:bg-[#0E0F10] leading-[12px] font-normal",
                  "pt-[10px] text-[10px] h-8 capitalize"
                )
              }

              return (
                <section
                  key={section.id}
                  className={classNames(
                    "border-b-[0.5px] dark:border-gray-600 last-of-type:border-none font-jakarta",
                    section.hideLabel ? "py-[8px]" : "pb-2.5"
                  )}
                >
                  {!section.hideLabel ? (
                    <section {...itemProps}>{sectionLabel}</section>
                  ) : null}

                  <section>
                    {renderSidebarMenuItemChildren(
                      items,
                      classification,
                      1,
                      classification === "open data"
                        ? 5
                        : Number(features?.[SIDEBAR_MAX_DEPTH])
                    )}
                  </section>
                </section>
              )
            })}
        </section>

        <section className={"sticky bottom-0 px-5"}>{asideFooter}</section>
      </section>
    </>
  )
}

export default SidebarV2
